import { AddressLocationTypeEnum, CompanyTypeEnum } from '@/graphql/generated/graphql';

export const DRAWER_WIDTH = 400;

const passwordRequirements = [
  { label: 'At least 12 characters long', test: (val: string) => val.length >= 12 },
  { label: 'One capital letter', test: (val: string) => /[A-Z]/.test(val) },
  { label: 'One lowercase letter', test: (val: string) => /[a-z]/.test(val) },
  { label: 'One number', test: (val: string) => /[0-9]/.test(val) },
  { label: 'One special character', test: (val: string) => /[^A-Za-z0-9]/.test(val) },
];

export { passwordRequirements };

export const BOX_SHADOW_1 = 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px';

export const DISABLE_SCROLLBAR = {
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none' /* Safari and Chrome */,
  },
};

export const EMOJIS = ['😀', '😁', '😆', '🤣', '🥰', '👍', '👎'];
export const DEFAULT_COMPANY_INFO = {
  id: '',
  name: '',
  email: '',
  phone: '',
  street: '',
  zip: '',
  companyNumber: '',
  tradingName: '',
  vatNumber: '',
  companyType: CompanyTypeEnum.Ltd,
};

export const COMPANY_TYPE_OPTIONS = [
  { value: CompanyTypeEnum.Ltd, label: 'Private Limited Company (LTD)' },
  { value: CompanyTypeEnum.Plc, label: 'Public Limited Company (PLC)' },
  { value: CompanyTypeEnum.Llp, label: 'Limited Liability Partnership (LLP)' },
  { value: CompanyTypeEnum.Lp, label: 'Limited Partnership (LP)' },
  { value: CompanyTypeEnum.GeneralPartnership, label: 'General Partnership' },
  { value: CompanyTypeEnum.SoleTrader, label: 'Sole Trader' },
  { value: CompanyTypeEnum.Cic, label: 'Community Interest Company (CIC)' },
  { value: CompanyTypeEnum.Cio, label: 'Charitable Incorporated Organisation (CIO)' },
];

// ==========================================
// Pagination
// ==========================================
export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZES = [25, 50, 100, 250, 500];
export const DEFAULT_PAGINATION_MODEL = { page: 0, pageSize: PAGE_SIZES[0] };

// ==========================================
// Files & Attachments
// ==========================================
export const MAX_FILE_SIZE_IN_MEGA_BYTES = 5.5;
export const MAX_ATTACHMENTS_ALLOWED = 5;

export const VALID_MS_EMBED_FORMATS = ['word', 'excel', 'powerpoint', 'pdf'];
export const VALID_DOCUMENTS_FILTER_EXTS = ['docx', 'doc', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'pdf'];

export const ACCEPTED_ATTACHMENT_FORMATS = [
  // Images
  'image/png',
  'image/jpeg',

  // Documents
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
].join(', ');

// ==========================================
// System, Auth & Regex
// ==========================================
export const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const AUTH_TOKEN_DURATION_IN_SECONDS = 1440; // 1 day
export const AUTH_TOKEN_LONGER_DURATION_IN_SECONDS = 7200; // 5 days
export const REFRESH_TOKEN_DURATION_IN_SECONDS = 43200; // 30 days
export const ENTITY_TYPE = ['Event', 'Project', 'Report'];

// ==========================================
// Address
// ==========================================
export const DEFAULT_COUNTRY = 'United Kingdom';
export const DEFAULT_ADDRESS = {
  id: '',
  street: '',
  city: '',
  state: '',
  country: DEFAULT_COUNTRY,
  zip: '',
  locationType: AddressLocationTypeEnum.Sub,
  title: '',
};

// ==========================================
// Time & Scheduling
// ==========================================
export const TIME_SLOTS = [
  '00:00',
  '01:00',
  '02:00',
  '03:00',
  '04:00',
  '05:00',
  '06:00',
  '07:00',
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
  '22:00',
  '23:00',
];

export const DEFAULT_START_TIME = '08:00';
export const DEFAULT_END_TIME = '17:00';
export const DEFAULT_PROJECT_SHIFTS =
  '[{ "name": "Day Shift", "startTime": "07:00", "endTime": "15:00" }, { "name": "Late Shift", "startTime": "15:00", "endTime": "23:00" }]';

export const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'text/plain': ['.txt'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/dwg': ['.dwg'],
  'application/dxf': ['.dxf'],
  'application/step': ['.step', '.stp'],
  'application/iges': ['.iges', '.igs'],
  'application/sat': ['.sat'],
  'application/x-sat': ['.sat'],
  'model/vnd.dwf': ['.dwf'],
  'application/x-autocad': ['.dwg', '.dxf'],
} as const;

export const ALLOWED_FILE_EXTENSIONS_TEXT =
  'PDF, Word (.doc, .docx), Excel (.xls, .xlsx), PowerPoint (.ppt, .pptx), Text (.txt), Images (.png, .jpg, .jpeg), CAD (.dwg, .dxf, .step, .stp, .iges, .igs, .sat, .dwf)';

export const DEFAULT_USER_DATA = {
  firstName: '',
  lastName: '',
  email: '',
  primaryPhone: '',
};
